import { vibeAppTelemetryEnv, type VibeAppTelemetryIdentity } from "./otel-telemetry";

/**
 * The ONE port a deployed Vibe app is reached on — the port its container
 * publishes, the port the health probe connects to, and the port the tenant's
 * Traefik routes to. Single-sourced because every executor has to agree on it,
 * and until this file existed each of them picked it independently.
 */

/**
 * Container port used when nothing better is known — no operator setting, and a
 * build that observed no port. Still the common case, so this is the port most
 * apps are deployed on.
 *
 * NOT derived from the image at deploy time: nothing reads the image's `EXPOSE`
 * when a container is placed, and the health-check schema's own comment used to
 * claim otherwise ("`null` = use the app's primary exposed port"). What the
 * platform CAN know is what the BUILD saw — see {@link vibeAppContainerPort} —
 * and beyond that the app is told the port through {@link VIBE_APP_PORT_ENV}
 * rather than being expected to guess.
 */
export const VIBE_APP_DEFAULT_CONTAINER_PORT = 8080;

/**
 * The env var naming the port the app must listen on. The universal PaaS
 * convention (Heroku, Railway, Render, Fly, Porter all set exactly this), which
 * is why an app that already runs on another platform runs here unchanged.
 *
 * **The platform's value wins over a user-set `PORT`, deliberately.** This names
 * the port the deployment ALREADY publishes, probes and routes — it is a fact
 * about the slot, not a preference. Letting an app override it would let the app
 * bind a port nothing connects to, which is precisely the failure this exists to
 * end: an app binding its own default while the probe connects to
 * {@link VIBE_APP_DEFAULT_CONTAINER_PORT} is reported "allocation unhealthy" and
 * rolled back, with nothing anywhere naming the port as the disagreement.
 * Changing the port is `healthCheckConfig.port`, and `PORT` follows it — one
 * knob, and the probe cannot end up pointed somewhere the app is not.
 */
export const VIBE_APP_PORT_ENV = "PORT";

/**
 * The port this app's container is reached on — published, probed and routed.
 *
 * Three sources, in strict precedence:
 *
 *   1. `healthCheck.port` — what an OPERATOR set. Always wins. It is the one
 *      value a human chose deliberately, and a detected port silently
 *      overriding it would make the knob a suggestion.
 *   2. `detectedPort` — what the BUILD observed the image listening on: the
 *      `EXPOSE` of the Dockerfile we generated, or of one the repo brought
 *      itself. This is the only route by which an app that hardcodes a port and
 *      ignores `$PORT` can work without someone hand-setting (1) — and until it
 *      existed, nothing told them to.
 *   3. {@link VIBE_APP_DEFAULT_CONTAINER_PORT} — the platform default, which the
 *      app is told about through {@link VIBE_APP_PORT_ENV}.
 *
 * `detectedPort` is nullable and optional so every legacy deployment — every row
 * written before the build reported one, and every Nixpacks build, which
 * observes nothing — lands on (3), i.e. exactly the behaviour that shipped
 * before this. Absent must never mean "undefined port".
 *
 * The probe must by construction connect where the app was told to listen, so
 * `vibeAppPlatformEnv` derives `PORT` from this same function with the same
 * inputs. Passing different arguments to the two would reintroduce the
 * disagreement they exist to end.
 */
export function vibeAppContainerPort(
  healthCheck: { port: number | null },
  detectedPort?: number | null
): number {
  return healthCheck.port ?? detectedPort ?? VIBE_APP_DEFAULT_CONTAINER_PORT;
}

/**
 * The broker's ORIGIN, e.g. `https://broker.nexus.gpt.nexus`. The Vibe SDK reads
 * it from the environment and appends its own paths; it is never a full URL.
 *
 * Declared here because this is the file that WRITES it, and pinned against the
 * SDK's own `BROKER_URL_ENV` by the broker module's
 * `coupon-env-name-matches-the-sdk.spec.ts`. The two ends are a cross-repository
 * contract with no compiler between them: the SDK reads `process.env` inside a
 * customer's container, the platform writes a Nomad jobspec, and a name that
 * disagrees by one character fails only at runtime, only inside a tenant.
 */
export const VIBE_APP_BROKER_URL_ENV = "NEXUS_BROKER_URL";

/**
 * The env var the Vibe SDK reads its single-use enrollment coupon from.
 *
 * DECLARED HERE, DELIBERATELY, EVEN THOUGH NOTHING IN THIS FILE INJECTS IT.
 * The name and the delivery are two different things: the name is a
 * cross-repository contract that must be checkable, and this file is the one the
 * name gate reads. The agent's jobspec builder imports this constant rather than
 * spelling the string itself, so a rename cannot land on one side only —
 * `coupon-env-name-matches-the-sdk.spec.ts` compares every broker env-var
 * literal in this file against the SDK's own source.
 *
 * That gate scans this file for LITERALS, so prose here must never spell a
 * broker variable name it does not mean — not even a wildcard stem, which its
 * regex reads as a name of its own and reports as a stranger the SDK does not
 * export.
 *
 * The VALUE is emphatically not injectable through {@link vibeAppPlatformEnv} —
 * see {@link VibeAppPlatformDeclaration} for why a coupon in a task's static
 * `Env` block turns every OOM restart into a credential-compromise page.
 */
export const VIBE_APP_ENROLLMENT_COUPON_ENV = "NEXUS_BROKER_ENROLLMENT_COUPON";

/**
 * What the PLATFORM declares about an app's slot, beyond its port.
 *
 * 🔴 THERE IS NO COUPON FIELD HERE, AND ADDING ONE IS THE BUG THIS SHAPE
 * PREVENTS.
 *
 * Everything this function returns becomes the task's static `Env` block, which
 * Nomad stores with the job and re-reads VERBATIM every time it restarts the
 * task — an OOM kill, a node drain, a crash loop — with no agent involvement at
 * all. That is correct for `PORT` and for the broker's origin, which are facts
 * about the slot and do not change between restarts.
 *
 * It is catastrophic for the enrollment coupon. A coupon is strictly single-use:
 * a second presentation answers `ENROLLMENT_REPLAY`, which is designed to page
 * as a credential compromise. Put it here and every routine restart pages, the
 * alert is muted inside a week, and then it detects nothing at all — the
 * detector is worse than absent, because it reads as present.
 *
 * So the coupon is delivered per TASK START instead, through a Nomad template
 * that re-renders on every start. It has no field on this object, which is what
 * makes the wrong delivery unrepresentable rather than merely discouraged.
 */
export interface VibeAppPlatformDeclaration {
  /**
   * The broker origin to publish, or null/absent for an app with no card
   * binding. Absent means the variable is not set at all rather than set empty:
   * the SDK's `fromEnv()` refuses a blank origin, and an app that was never
   * given one should fail with "not configured", not "configured wrongly".
   */
  readonly brokerUrl?: string | null;
  /**
   * Who this slot IS, for telemetry — `OTEL_SERVICE_NAME` and
   * `OTEL_RESOURCE_ATTRIBUTES`. Absent means neither variable is set.
   *
   * Static facts about the slot, so unlike the collector's address they belong
   * in the task's `Env` and need no Nomad resolution. The address is the half
   * that cannot be composed by a caller: the collector has no DNS record and no
   * load balancer, so only Nomad's own service registry can answer it, and only
   * from inside a `template` stanza in the jobspec.
   *
   * ONLY A CALLER THAT ALSO SUPPLIES THAT ADDRESS SHOULD PASS THIS. Identity
   * without an endpoint is a set of variables naming a collector the app is
   * never told how to reach — inert, and indistinguishable from wiring that
   * works.
   */
  readonly telemetry?: VibeAppTelemetryIdentity | null;
}

/**
 * The environment the platform imposes on every app container, whatever else the
 * user has set. Returned as a record so callers spread it LAST over user env and
 * secrets, making the precedence visible at the call site instead of implied.
 *
 * Takes the same first two inputs as {@link vibeAppContainerPort} and must be
 * called with the same values — `PORT` naming a different port from the one the
 * slot publishes is the exact failure this pair exists to prevent.
 *
 * See {@link VibeAppPlatformDeclaration} for why the enrollment coupon is
 * deliberately not expressible here.
 */
export function vibeAppPlatformEnv(
  healthCheck: { port: number | null },
  detectedPort?: number | null,
  platform?: VibeAppPlatformDeclaration
): Record<string, string> {
  return {
    [VIBE_APP_PORT_ENV]: String(vibeAppContainerPort(healthCheck, detectedPort)),
    ...(platform?.brokerUrl ? { [VIBE_APP_BROKER_URL_ENV]: platform.brokerUrl } : {}),
    ...(platform?.telemetry ? vibeAppTelemetryEnv(platform.telemetry) : {})
  };
}
